import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import MuscleHeadTextIcon from "../../assets/MuscleHeadTextIcon";
import { Link as RouterLink } from "react-router-dom";
import { AlertContext } from "@context/AlertProvider";
import LanguageSelect from "../shared-theme/components/LanguageSelect";
import { getOurSupportEmail } from "@config/config"
import { useSetNewPasswordMutation } from "@mutation/authMutation";
import AppTheme from "../shared-theme/AppTheme";
import { AuthContext } from "@context/AuthProvider";


const Card = styled(MuiCard)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignSelf: "center",
    width: "100%",
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: "auto",
    [theme.breakpoints.up("sm")]: {
        maxWidth: "450px",
    },
    boxShadow:
        "hsla(350, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(350, 25%, 10%, 0.05) 0px 15px 35px -5px",
    ...theme.applyStyles("dark", {
        boxShadow:
            "hsla(350, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(350, 25%, 10%, 0.08) 0px 15px 35px -5px",
    }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
    minHeight: "100vh",
    padding: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
        padding: theme.spacing(4),
    },
    position: "relative",
    "&::before": {
        content: '""',
        display: "block",
        position: "absolute",
        zIndex: -1,
        inset: 0,
        backgroundImage:
            "radial-gradient(ellipse at 50% 50%, hsl(340, 100%, 97%), hsl(0, 0%, 100%))",
        backgroundRepeat: "no-repeat",
        ...theme.applyStyles("dark", {
            backgroundImage:
                "radial-gradient(at 50% 50%, hsla(340, 100%, 16%, 0.5), hsla(350, 30%, 5%))",
        }),
    },
}));


export default function SetNewPassword(props) {
    const [password, setPassword] = useState("");
    const [passwordError, setPasswordError] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
    const { t } = useTranslation("auth");
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const code = searchParams.get("code") || "";
    const setNewPasswordMutation = useSetNewPasswordMutation();
    const { addAlertBanner } = React.useContext(AlertContext);
    const { setAccessToken, setRefreshToken } = React.useContext(AuthContext);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validateInputs()) return;

        setNewPasswordMutation.mutate(
            { data: { code: code, new_password: password } },
            {
                onSuccess: (response) => {
                    setAccessToken(response.data.access_token);  
                    setRefreshToken(response.data.refresh_token); 
                    navigate("/"); // Redirect to login after success
                    addAlertBanner(t("setNewPasswordSuccess"), "success");
                },
                onError: () => {
                    setPasswordError(true);
                    setPasswordErrorMessage(t("passwordUpdateFailed"));
                },
            }
        );
    };

    const validateInputs = () => {
        if (password.length < 8) {
            setPasswordError(true);
            setPasswordErrorMessage(t("passwordTooShort"));
            return false;
        }
        setPasswordError(false);
        setPasswordErrorMessage("");
        return true;
    };

    return (
        <AppTheme {...props}>
            <CssBaseline enableColorScheme />
            <SignInContainer direction="column" justifyContent="space-between">

                <Box
                    sx={{
                        position: "fixed",
                        top: "1rem",
                        right: "1rem",
                        display: "flex",
                        gap: "0.5rem",
                    }}
                >
                    <LanguageSelect />
                </Box>
                <Card variant="outlined">
                    <MuscleHeadTextIcon
                        sx={{
                            alignSelf: "center",
                            height: "160px",
                            width: "100%",
                            marginBottom: "40px",
                        }}
                    />
                    <Typography
                        component="h1"
                        variant="h4"
                        sx={{ width: "100%", fontSize: "clamp(2rem, 10vw, 2.15rem)" }}
                    >
                        {t("setNewPassword")}
                    </Typography>
                    <Box
                        component="form"
                        onSubmit={handleSubmit}
                        noValidate
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            gap: 2,
                        }}
                    >
                        <FormControl>
                            <FormLabel htmlFor="password">{t("password")}</FormLabel>
                            <TextField
                                error={passwordError}
                                helperText={passwordErrorMessage}
                                name="password"
                                placeholder="••••••"
                                type="password"
                                id="password"
                                autoComplete="current-password"
                                autoFocus
                                required
                                fullWidth
                                variant="outlined"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </FormControl>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            // onClick={validateInputs}
                            sx={{
                                marginTop: 4
                            }}
                        >
                            {t("send")}
                        </Button>
                    </Box>

                    <Link
                        component={RouterLink} to="/signin"
                        type="button"
                        variant="body2"

                        sx={{ alignSelf: "center", marginTop: 5 }}
                    >
                        {t("backToLogin", { context: "button" })}
                    </Link>

                    <Link
                        type="button"
                        variant="body2"
                        href={"mailto:" + getOurSupportEmail()}
                        sx={{ alignSelf: "center", color: "gray" }} // TODO: link the color with the theme
                    >
                        {t("contactUs")}
                    </Link>
                </Card>
            </SignInContainer>
        </AppTheme>
    );
}